import React, { useCallback, useMemo } from 'react';
import { formatExpressionVariableName } from '@shared/helpers/expression-variables.helpers';
import InlineTextField, { InlineTextFieldProps } from '@ui/InlineTextField';
import { getPropertyDisplayName } from '@shared/helpers/element-property-display-name.helpers';
import { OutlinedTextFieldProps } from '@mui/material';
import { IElementProperty } from '@shared/models/element-property.model';
import { OneOfPropertyElements } from '@shared/models/project.model';
import { useUIState } from '@client/store/ui/ui.hook';
import { isValidVariableName, isAvailableName } from '@shared/helpers/mathjs';
import {
  useElementProperties,
  useUpdateProperty,
} from '@client/hooks/use-element-properties.hooks';

interface ElementPropertyNameInputProps extends Omit<
  InlineTextFieldProps,
  'value' | 'textFieldProps' | 'formatInput' | 'onSave'
> {
  property: IElementProperty;
  element: OneOfPropertyElements;
  editing: boolean;
  onSave: () => void;
}

const ElementPropertyNameInput: React.FC<ElementPropertyNameInputProps> = ({
  property,
  element,
  autoFocus,
  editing,
  error,
  onSave,
  onChange,
  ...rest
}) => {
  const { setAddedPropertyId } = useUIState('setAddedPropertyId');
  const updateProperty = useUpdateProperty(element);
  const properties = useElementProperties(element);

  const handleSave = useCallback(
    async (name: string) => {
      if (error) return;
      await updateProperty({ ...property, name });
      onSave?.();
    },
    [error, updateProperty, property, onSave],
  );

  const handleChange = useCallback(
    (value?: string) => {
      if (!value) {
        return;
      }
      const expressionName = formatExpressionVariableName(value);

      const nameIsValid =
        isValidVariableName(expressionName) && isAvailableName(expressionName);

      if (!nameIsValid) {
        onChange?.('Name is invalid');
        return;
      }
      if (properties.some((p) => p.id !== property.id && p.name === value)) {
        onChange?.('Name is taken');
        return;
      }
      onChange?.('');
    },
    [onChange, properties, property],
  );

  const textFieldProps = useMemo<Partial<OutlinedTextFieldProps>>(
    () => ({
      autoFocus,
      onFocus: (e) => {
        if (autoFocus) {
          e.currentTarget.select();
        }
      },
      onBlur: () => {
        if (autoFocus) {
          setAddedPropertyId(undefined);
        }
      },
    }),
    [autoFocus, setAddedPropertyId],
  );

  return (
    <InlineTextField
      {...rest}
      variant="body2"
      editing={editing}
      error={error}
      textFieldProps={textFieldProps}
      value={getPropertyDisplayName(property, element)}
      formatInput={formatExpressionVariableName}
      onChange={handleChange}
      onSave={handleSave}
    />
  );
};

export default ElementPropertyNameInput;
